import type { BigNumber } from '@ethersproject/bignumber';
import { Percent } from '@uniswap/sdk-core';
import { Position } from '@uniswap/v3-sdk';
import { USDC_MAINNET, USDT, WRAPPED_NATIVE_CURRENCY } from 'constants/tokens';
import { useMemo } from 'react';

import { useFormatter } from 'utils/formatNumbers';
import { unwrappedToken } from 'utils/unwrappedToken';

import { useToken } from 'hooks/Tokens';
import { usePool } from 'hooks/usePools';

import { RefreshIcon, TractorIcon } from 'components/Icons';
import Spinner from 'components/Icons/Spinner';
import { AssetLogoSize, AssetLogoVariant } from 'components/Logo/AssetLogo';
import DoubleTokenLogo from 'components/composed/Logo/DoubleTokenLogo';
import type { TokenLogoData } from 'components/composed/Logo/Logo.types';
import { RangeBadge } from 'components/composed/RangeBadge';

type getPriceOrderingFromPositionForUIProps = {
  position?: Position;
};

const getPriceOrderingFromPositionForUI = ({
  position
}: getPriceOrderingFromPositionForUIProps) => {
  if (!position) {
    return {};
  }

  const token0 = position.amount0.currency;
  const token1 = position.amount1.currency;

  // if token0 is a dollar-stable asset, set it as the quote token
  const stables = [USDC_MAINNET, USDT];
  if (stables.some((stable) => stable.equals(token0))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1
    };
  }

  // if token1 is an ETH-/BTC-stable asset, set it as the base token
  const bases = [...Object.values(WRAPPED_NATIVE_CURRENCY)];
  if (bases.some((base) => base && base.equals(token1))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1
    };
  }

  // if both prices are below 1, invert
  if (position.token0PriceUpper.lessThan(1)) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1
    };
  }

  // otherwise, just return the default
  return {
    priceLower: position.token0PriceLower,
    priceUpper: position.token0PriceUpper,
    quote: token1,
    base: token0
  };
};

type PoolPositionItemProps = {
  chainId: number;
  tokenData0: TokenLogoData;
  tokenData1: TokenLogoData;
  fee: number;
  liquidity: BigNumber;
  tickLower: number;
  tickUpper: number;
  rewards?: number;
  isAutoHarvesting: boolean;
  isAutoCompounding: boolean;
  harvesterStatusesLoading: boolean;
};

export const PoolPositionItem = ({
  chainId,
  tokenData0,
  tokenData1,
  fee,
  liquidity,
  tickLower,
  tickUpper,
  rewards,
  isAutoHarvesting,
  isAutoCompounding,
  harvesterStatusesLoading
}: PoolPositionItemProps) => {
  const { formatDelta } = useFormatter();

  const token0 = useToken(tokenData0.address);
  const token1 = useToken(tokenData1.address);

  const currency0 = token0 ? unwrappedToken(token0) : undefined;
  const currency1 = token1 ? unwrappedToken(token1) : undefined;

  // Construct Position from details returned
  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, fee);

  const position = useMemo(() => {
    if (pool) {
      return new Position({
        pool,
        liquidity: liquidity.toString(),
        tickLower,
        tickUpper
      });
    }
    return undefined;
  }, [liquidity, pool, tickLower, tickUpper]);

  const { quote, base } = getPriceOrderingFromPositionForUI({ position });

  const currencyQuote = quote && unwrappedToken(quote);
  const currencyBase = base && unwrappedToken(base);

  const removed = liquidity?.eq(0);

  const outOfRange: boolean = pool
    ? pool.tickCurrent < tickLower || pool.tickCurrent >= tickUpper
    : false;

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-row justify-between'>
        <div className='flex flex-row items-center gap-1'>
          <DoubleTokenLogo
            chainId={chainId}
            token0={tokenData0}
            token1={tokenData1}
            size={AssetLogoSize.sm}
            variant={AssetLogoVariant.Secondary}
            showBorder
          />

          <span className='text-sm font-medium'>
            {currencyQuote ? currencyQuote.symbol : '????'}/
            {currencyBase ? currencyBase.symbol : '????'}
          </span>

          <div className='flex h-4.5 items-center rounded bg-blue-900 px-1 py-0.5 text-xs font-medium text-blue-200'>
            {formatDelta(
              parseFloat(new Percent(fee, 1_000_000).toSignificant())
            )}
          </div>
        </div>

        <div className='flex flex-row items-center gap-1'>
          {harvesterStatusesLoading && (
            <Spinner className='size-4 stroke-gray-100' />
          )}
          {!harvesterStatusesLoading && isAutoHarvesting && (
            <TractorIcon className='size-4 fill-gray-600' />
          )}
          {!harvesterStatusesLoading && isAutoCompounding && (
            <RefreshIcon className='size-4 fill-gray-600' />
          )}
        </div>
      </div>

      <div className='flex flex-row justify-between'>
        <div className='flex flex-row items-center gap-1 text-xs font-bold'>
          <div className='text-gray-600'>Rewards:</div>
          <div className='text-gray-100'>{rewards ? rewards : '--'}</div>
        </div>

        <div className='flex flex-row items-center gap-1'>
          <RangeBadge removed={removed} inRange={!outOfRange} />
        </div>
      </div>
    </div>
  );
};
